import { httpClient } from '../client';
import { BaseEndpoint } from './base.endpoint';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import type {
  Invoice,
  PaymentSettings,
  WalletBalance,
  WalletTransaction,
  Payout,
  Refund,
  PaymentSessionResponse,
  OnboardingResponse,
  CreateInvoiceRequest,
  CreateRefundRequest,
  WithdrawalRequest,
  StartOnboardingRequest,
  ListInvoicesParams,
  ListTransactionsParams,
  ListPayoutsParams,
  ListRefundsParams,
  DataResponse,
  InvoiceNotificationPayload,
} from '../../../types/payment';

/**
 * PaymentEndpoints - Handles all payment-related API calls
 * Singleton pattern following existing endpoint conventions
 */
class PaymentEndpoints extends BaseEndpoint {
  private static instance: PaymentEndpoints;

  private constructor() {
    super('organisations');
  }

  static getInstance(): PaymentEndpoints {
    if (!PaymentEndpoints.instance) {
      PaymentEndpoints.instance = new PaymentEndpoints();
    }
    return PaymentEndpoints.instance;
  }

  // ==================== ONBOARDING ====================

  async startOnboarding(
    organizationId: string,
    data: StartOnboardingRequest
  ): Promise<DataResponse<OnboardingResponse>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<DataResponse<OnboardingResponse>>(
        `${this.basePath}/${organizationId}/payment/onboard`,
        data,
        { headers }
      );

      logger.info('Onboarding started successfully', { organizationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to start onboarding:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async refreshOnboardingLink(
    organizationId: string,
    refreshUrl: string,
    returnUrl: string
  ): Promise<DataResponse<OnboardingResponse>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<OnboardingResponse>>(
        `${this.basePath}/${organizationId}/payment/onboard/refresh?refreshUrl=${encodeURIComponent(
          refreshUrl
        )}&returnUrl=${encodeURIComponent(returnUrl)}`,
        { headers }
      );

      logger.info('Onboarding link refreshed', { organizationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to refresh onboarding link:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getPaymentSettings(organizationId: string): Promise<DataResponse<PaymentSettings>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<PaymentSettings>>(
        `${this.basePath}/${organizationId}/payment/settings`,
        { headers }
      );

      logger.info('Payment settings fetched successfully', { organizationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch payment settings:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async updatePaymentSettings(
    organizationId: string,
    data: Partial<PaymentSettings>
  ): Promise<DataResponse<PaymentSettings>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.patch<DataResponse<PaymentSettings>>(
        `${this.basePath}/${organizationId}/payment/settings`,
        data,
        { headers }
      );

      logger.info('Payment settings updated successfully', { organizationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to update payment settings:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async syncAccountStatus(organizationId: string): Promise<DataResponse<PaymentSettings>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<DataResponse<PaymentSettings>>(
        `${this.basePath}/${organizationId}/payment/sync`,
        {},
        { headers }
      );

      logger.info('Account status synced successfully', { organizationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync account status:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // ==================== INVOICES ====================

  async listInvoices(
    organizationId: string,
    params?: ListInvoicesParams
  ): Promise<{ data: { invoices: Invoice[]; total: number; page: number; pageSize: number } }> {
    try {
      const headers = await this.getAuthHeaders();
      const queryString = params ? new URLSearchParams(params as any).toString() : '';
      const url = `${this.basePath}/${organizationId}/invoices${queryString ? '?' + queryString : ''}`;

      const response = await httpClient.get<{
        data: { invoices: Invoice[]; total: number; page: number; pageSize: number };
      }>(url, { headers });

      logger.info('Invoices fetched successfully', {
        organizationId,
        count: response.data.data.total,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch invoices:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getInvoiceById(organizationId: string, invoiceId: string): Promise<DataResponse<Invoice>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<Invoice>>(
        `${this.basePath}/${organizationId}/invoices/${invoiceId}`,
        { headers }
      );

      logger.info('Invoice fetched successfully', { organizationId, invoiceId });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch invoice:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getInvoiceByBooking(
    organizationId: string,
    bookingId: string
  ): Promise<DataResponse<Invoice>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<Invoice>>(
        `${this.basePath}/${organizationId}/bookings/${bookingId}/invoice`,
        { headers }
      );

      logger.info('Booking invoice fetched successfully', { organizationId, bookingId });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch booking invoice:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createInvoice(
    organizationId: string,
    data: CreateInvoiceRequest
  ): Promise<DataResponse<Invoice>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<DataResponse<Invoice>>(
        `${this.basePath}/${organizationId}/invoices`,
        data,
        { headers }
      );

      logger.info('Invoice created successfully', {
        organizationId,
        invoiceId: response.data.data.id,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to create invoice:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createPaymentSession(
    organizationId: string,
    invoiceId: string
  ): Promise<DataResponse<PaymentSessionResponse>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<DataResponse<PaymentSessionResponse>>(
        `${this.basePath}/${organizationId}/invoices/${invoiceId}/payment-session`,
        {},
        { headers }
      );

      logger.info('Payment session created successfully', { organizationId, invoiceId });
      return response.data;
    } catch (error) {
      logger.error('Failed to create payment session:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async sendInvoice(organizationId: string, invoiceId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.post(
        `${this.basePath}/${organizationId}/invoices/${invoiceId}/send`,
        {},
        { headers }
      );

      logger.info('Invoice sent successfully', { organizationId, invoiceId });
    } catch (error) {
      logger.error('Failed to send invoice:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async resendInvoice(organizationId: string, payload: InvoiceNotificationPayload): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.post(`${this.basePath}/${organizationId}/resend-invoice`, payload, {
        headers,
      });

      logger.info('Invoice resent successfully', {
        organizationId,
        invoiceId: payload.invoiceId,
      });
    } catch (error) {
      logger.error('Failed to resend invoice:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async downloadInvoice(
    organizationId: string,
    invoiceId: string
  ): Promise<DataResponse<{ urls: { pdf: string } }>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<{ urls: { pdf: string } }>>(
        `${this.basePath}/${organizationId}/invoices/${invoiceId}/download`,
        { headers }
      );

      logger.info('Invoice download URL fetched successfully', { organizationId, invoiceId });
      return response.data;
    } catch (error) {
      logger.error('Failed to get invoice download URL:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // ==================== WALLET ====================

  async getWalletBalance(organizationId: string): Promise<DataResponse<WalletBalance>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<WalletBalance>>(
        `${this.basePath}/${organizationId}/wallet/balance`,
        { headers }
      );

      logger.info('Wallet balance fetched successfully', { organizationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch wallet balance:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getWalletTransactions(
    organizationId: string,
    params?: ListTransactionsParams
  ): Promise<{ data: { transactions: WalletTransaction[]; total: number } }> {
    try {
      const headers = await this.getAuthHeaders();
      const queryString = params ? new URLSearchParams(params as any).toString() : '';
      const url = `${this.basePath}/${organizationId}/wallet/transactions${queryString ? '?' + queryString : ''}`;

      const response = await httpClient.get<{
        data: { transactions: WalletTransaction[]; total: number };
      }>(url, { headers });

      logger.info('Wallet transactions fetched successfully', {
        organizationId,
        count: response.data.data.total,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch wallet transactions:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async listPayouts(
    organizationId: string,
    params?: ListPayoutsParams
  ): Promise<{ data: { payouts: Payout[]; total: number } }> {
    try {
      const headers = await this.getAuthHeaders();
      const queryString = params ? new URLSearchParams(params as any).toString() : '';
      const url = `${this.basePath}/${organizationId}/wallet/payouts${queryString ? '?' + queryString : ''}`;

      const response = await httpClient.get<{ data: { payouts: Payout[]; total: number } }>(url, {
        headers,
      });

      logger.info('Payouts fetched successfully', {
        organizationId,
        count: response.data.data.total,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch payouts:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async requestWithdrawal(
    organizationId: string,
    data: WithdrawalRequest
  ): Promise<DataResponse<Payout>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<DataResponse<Payout>>(
        `${this.basePath}/${organizationId}/wallet/withdraw`,
        data,
        { headers }
      );

      logger.info('Withdrawal requested successfully', {
        organizationId,
        amount: data.amountCents,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to request withdrawal:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // ==================== REFUNDS ====================

  async listRefunds(
    organizationId: string,
    params?: ListRefundsParams
  ): Promise<{ data: { refunds: Refund[]; total: number } }> {
    try {
      const headers = await this.getAuthHeaders();
      const queryString = params ? new URLSearchParams(params as any).toString() : '';
      const url = `${this.basePath}/${organizationId}/refunds${queryString ? '?' + queryString : ''}`;

      const response = await httpClient.get<{ data: { refunds: Refund[]; total: number } }>(url, {
        headers,
      });

      logger.info('Refunds fetched successfully', {
        organizationId,
        count: response.data.data.total,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch refunds:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getRefundById(organizationId: string, refundId: string): Promise<DataResponse<Refund>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<DataResponse<Refund>>(
        `${this.basePath}/${organizationId}/refunds/${refundId}`,
        { headers }
      );

      logger.info('Refund fetched successfully', { organizationId, refundId });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch refund:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createRefund(
    organizationId: string,
    invoiceId: string,
    data: CreateRefundRequest
  ): Promise<DataResponse<Refund>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<DataResponse<Refund>>(
        `${this.basePath}/${organizationId}/invoices/${invoiceId}/refund`,
        data,
        { headers }
      );

      logger.info('Refund created successfully', {
        organizationId,
        invoiceId,
        refundId: response.data.data.id,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to create refund:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const paymentEndpoints = PaymentEndpoints.getInstance();
